import { morph } from '@theme/morph'
import { useReduced } from '@theme/utils'

const InfiniteButtonConfig = {
  attributes: {
    busy: 'aria-busy'
  },
  modes: {
    infinite: 'infinite'
  },
  rootMargin: '200px 0px 200px 0px',
  selectors: {
    grid: '#ProductGridContainer',
    pagination: '.pagination'
  }
}

class InfiniteButton extends HTMLButtonElement {
  #abortController = null
  #intersectionObserver = null

  connectedCallback() {
    this.handleClick = this.handleClick.bind(this)
    this.addEventListener('click', this.handleClick)

    if (
      this.getAttribute('mode') === InfiniteButtonConfig.modes.infinite &&
      !useReduced()
    ) {
      this.#observeForInfinite()
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick)
    this.#abortController?.abort()
    this.#intersectionObserver?.disconnect()
  }

  #observeForInfinite() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !this.isBusy) {
          this.handleClick()
        }
      },
      { rootMargin: InfiniteButtonConfig.rootMargin }
    )
    this.#intersectionObserver.observe(this)
  }

  get isBusy() {
    return this.hasAttribute(InfiniteButtonConfig.attributes.busy)
  }

  setBusy(isBusy) {
    if (isBusy) {
      this.setAttribute(InfiniteButtonConfig.attributes.busy, 'true')
      this.classList.add('pointer-events-none')
      return
    }

    this.removeAttribute(InfiniteButtonConfig.attributes.busy)
    this.classList.remove('pointer-events-none')
  }

  async handleClick(event) {
    event?.preventDefault?.()
    if (this.isBusy) return

    const action = this.getAttribute('action')
    if (!action) return

    this.#abortController?.abort()
    this.#abortController = new AbortController()
    this.setBusy(true)

    try {
      const response = await fetch(action, {
        signal: this.#abortController.signal
      })
      const text = await response.text()
      this.#applyResponse(text)
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.setBusy(false)
      }
    }
  }

  #applyResponse(text) {
    const parsed = new DOMParser().parseFromString(text, 'text/html')
    const currentGrid = document.querySelector(
      InfiniteButtonConfig.selectors.grid
    )
    const incomingGrid = parsed.querySelector(
      InfiniteButtonConfig.selectors.grid
    )
    if (!(currentGrid && incomingGrid)) {
      this.setBusy(false)
      return
    }

    const currentPagination = currentGrid.querySelector(
      InfiniteButtonConfig.selectors.pagination
    )
    const incomingPagination = incomingGrid.querySelector(
      InfiniteButtonConfig.selectors.pagination
    )

    const currentList = currentGrid.querySelector('motion-list, .card-grid')
    const incomingList = incomingGrid.querySelector('motion-list, .card-grid')

    if (currentList && incomingList) {
      for (const card of Array.from(incomingList.children)) {
        currentList.appendChild(card)
      }
    }

    if (currentPagination && incomingPagination) {
      morph(currentPagination, incomingPagination)
      return
    }

    currentPagination?.remove()
  }
}

if (!customElements.get('ui-infinite-button')) {
  customElements.define('ui-infinite-button', InfiniteButton, {
    extends: 'button'
  })
}
